import CustomTooltip from '../../../shared/CustomTooltip';
import React, { useState } from 'react';

import { HelpOutline } from '@mui/icons-material';
import {
    Badge,
    Box,
    List,
    ListItem,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import FormUpload from '../../../shared/FormUpload';
import { Media } from '../../../types/user';
import ProfilePicture from '../ProfilePicture';
import ProfilePictureModal from './Profile Picture/ProfilePictureModal';

interface UserProps {
    firstName: string;
    lastName: string;
    email: string;
    profilePicture?: {
        src: string;
    };
}

interface ProfileProps {
    user: UserProps;
    onSubmit: (data: Media) => void;
    message?: boolean;
}

const EditProfilePicture = ({
    user,
    onSubmit,
    message = true,
}: ProfileProps) => {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const theme = useTheme();

    const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false);
    const [photo, setPhoto] = useState<File>();
    const [updatedPhoto, setUpdatedPhoto] = useState('');

    const { control } = useForm({
        defaultValues: {
            profilePictures: [] as Media[],
        },
    });

    const handleFileUpload = async (
        files: File[],
    ): Promise<Media[] | undefined> => {
        if (files.length > 0) {
            const file = files[0];
            setPhoto(file);
            setUploadModalOpen(true);

            // Return empty array since we'll handle the upload in the modal
            return [];
        }
        return undefined;
    };

    const onClose = () => {
        setPhoto(undefined);
        setUploadModalOpen(false);
    };

    const handleSubmit = (photo: Media) => {
        setUpdatedPhoto(photo.src);
        onSubmit(photo);
    };

    return (
        <>
            <Stack
                direction={isMobile ? 'column' : 'row'}
                alignItems='center'
                gap='30px'
                sx={{
                    py: '20px',
                    borderRadius: '12px',
                    width: '100%',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: isMobile ? 'center' : 'flex-start',
                        alignSelf: isMobile ? 'center' : 'auto',
                    }}
                >
                    <Badge
                        overlap='circular'
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        badgeContent={
                            <CustomTooltip
                                title={
                                    <Typography>
                                        <List>
                                            <ListItem>
                                                {' '}
                                                Your profile picture will be
                                                used for facial recognition.
                                            </ListItem>
                                            <ListItem>
                                                {' '}
                                                For security reasons, a profile
                                                picture is mandatory for some
                                                experiences.
                                            </ListItem>
                                        </List>
                                    </Typography>
                                }
                                placement='right'
                            >
                                <HelpOutline
                                    sx={{
                                        bgcolor: theme.palette.neutral.main,
                                        borderRadius: '24px',
                                        padding: '1px',
                                        fontSize: '24px',
                                    }}
                                />
                            </CustomTooltip>
                        }
                    >
                        <ProfilePicture
                            profileDetails={user}
                            src={
                                updatedPhoto
                                    ? updatedPhoto
                                    : user?.profilePicture?.src
                            }
                        />
                    </Badge>
                </Box>
                <Box
                    display={isMobile ? 'flex' : 'block'}
                    flexDirection='column'
                    sx={{
                        flex: 1,
                        width: '100%',
                    }}
                >
                    <FormUpload
                        name='profilePictures'
                        control={control}
                        onUpload={handleFileUpload}
                        showFiles={false}
                        validation={{
                            acceptedTypes: {
                                'image/png': ['.png', '.jpg', '.jpeg'],
                            },
                            maxFileSize: 5 * 1024 * 1024, // 5MB
                            maxFiles: 1,
                        }}
                        uploadText='Click to browse files or drag and drop'
                        helperText='PNG or JPG up to 5MB'
                        hasLightBox={false}
                    />
                    {user &&
                        !user?.profilePicture &&
                        !updatedPhoto &&
                        message && (
                            <Typography
                                variant='subtitle2'
                                color='red'
                                pt='10px'
                            >
                                To complete your profile, please upload a
                                profile picture!
                            </Typography>
                        )}
                </Box>
            </Stack>
            <ProfilePictureModal
                open={uploadModalOpen}
                onClose={onClose}
                onSubmit={handleSubmit}
                photo={photo}
            />
        </>
    );
};

export default EditProfilePicture;
